import React, { useState, useEffect } from 'react';
import FightingArts from '../All Cards/FightingArts';
import SecretFightingArts from '../All Cards/SecretFightingArts';
import StrainFightingArts from '../All Cards/StrainFightingArts';

import { usePersistedState } from "./SheetState";


const CharacterSheets = ({ showFightingArts, showHuntXP, storageKey } = {}) => {
  const keyBase = storageKey || 'kdm_characterSheets__single';

  const [huntXP, setHuntXP] = usePersistedState(`${keyBase}__huntXP`, Array(16).fill(false));
  const [retired, setRetired] = usePersistedState(`${keyBase}__retired`, false);

  const handleHuntXPChange = (index) => {
    setHuntXP((prev) => {
      const updated = [...prev];
      updated[index] = !updated[index];
      return updated;
    });
  };

  const categories = {
    "Fighting Arts": FightingArts,
    "Secret Fighting Arts": SecretFightingArts,
    "Strain Fighting Arts": StrainFightingArts,
  };

  const [selectedArts, setSelectedArts] = usePersistedState(`${keyBase}__selectedArts`, [null, null, null]);
  const [selectedCategories, setSelectedCategories] = usePersistedState(`${keyBase}__selectedCategories`, ['', '', '']);
  const [observationStates, setObservationStates] = usePersistedState(`${keyBase}__observationStates`, {});
  const [cannotUseFightingArts, setCannotUseFightingArts] = usePersistedState(`${keyBase}__cannotUseFightingArts`, false);

  const handleCategoryChange = (index, category) => {
    const newCategories = [...selectedCategories];
    newCategories[index] = category;
    setSelectedCategories(newCategories);

    setSelectedArts((prev) => {
      const updatedArts = [...prev];
      updatedArts[index] = null;
      return updatedArts;
    });

    setObservationStates((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleArtChange = (index, art) => {
    const updatedArts = [...selectedArts];
    if (art === 'random' && selectedCategories[index]) {
      const availableArts = categories[selectedCategories[index]] || [];
      if (availableArts.length > 0) {
        updatedArts[index] = availableArts[Math.floor(Math.random() * availableArts.length)];
      }
    } else {
      const list = categories[selectedCategories[index]] || [];
      updatedArts[index] = list.find((item) => item.name === art) || null;
    }
    setSelectedArts(updatedArts);

    setObservationStates((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const toggleObservation = (index, boxIndex) => {
    setObservationStates((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [boxIndex]: !prev[index]?.[boxIndex],
      },
    }));
  };

  return (
    <>
      {showHuntXP && (
        <div className="hunt-xp-wrapper">
          <div className="hunt-xp-container">
            <label className="hunt-xp-label">Hunt XP:</label>
            <div className="hunt-xp-checkboxes">
              {Array.from({ length: 16 }, (_, index) => (
                <input
                  key={index}
                  type="checkbox"
                  checked={huntXP[index]}
                  onChange={() => handleHuntXPChange(index)}
                  className={`hunt-xp-checkbox ${
                    [1, 5, 9, 14].includes(index) ? 'bold' : ''
                  } ${index === 15 ? 'extra-bold' : ''}`}
                />
              ))}
            </div>
          </div>

          <div className="age-milestones">
            {[1, 2, 3, 4].map((num) => (
              <div key={num} className="age-group">
                {Array.from({ length: num }).map((_, idx) => (
                  <input key={idx} type="checkbox" defaultChecked disabled className="hunt-xp-checkbox bold" />
                ))}
                <span>📖 Age {num}</span>
              </div>
            ))}

            <div className="retired-section">
              <input
                type="checkbox"
                className="hunt-xp-checkbox extra-bold"
                checked={retired}
                onChange={() => setRetired(!retired)}
              />
              <span className="retired-label">Retired</span>
            </div>
          </div>
        </div>
      )}

      {showFightingArts && (
        <div className="fightingArts">
          <div className="fightingArts-header">
            <label>
              Fighting Arts: <span className="max-text">(Maximum 3)</span>
            </label>
            <label>
              <input
                type="checkbox"
                checked={cannotUseFightingArts}
                onChange={() => setCannotUseFightingArts(!cannotUseFightingArts)}
              />
              🔒 <span className="cannot-use-text">Cannot use Fighting Arts</span>
            </label>
          </div>

          {[0, 1, 2].map((index) => (
            <div key={index} className="fightingart-dropdown-section">
              <select
                value={selectedCategories[index]}
                onChange={(e) => handleCategoryChange(index, e.target.value)}
                disabled={cannotUseFightingArts}
                className="fightingart-dropdown"
              >
                <option value="">Select Category</option>
                {Object.keys(categories).map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>

              {selectedCategories[index] && (
                <select
                  value={selectedArts[index]?.name || ''}
                  onChange={(e) => handleArtChange(index, e.target.value)}
                  disabled={cannotUseFightingArts}
                  className="fightingart-dropdown"
                >
                  <option value="">Select Fighting Art</option>
                  <option value="random">🎲 Random Selection</option>
                  {(categories[selectedCategories[index]] || []).map((art) => (
                    <option key={art.name} value={art.name}>
                      {art.name}
                    </option>
                  ))}
                </select>
              )}

              {selectedArts[index] && (
                <div className="art-details">
                  <strong>{selectedArts[index].name}</strong>
                  {selectedArts[index].definition && <p>{selectedArts[index].definition}</p>}
                  {selectedArts[index].rules && <p>{selectedArts[index].rules}</p>}

                  {selectedArts[index].observationCondition && (
                    <div className="observation-container">
                      <div className="observation-text">{selectedArts[index].observationCondition}</div>
                      <div className="observation-checkboxes">
                        {Array.from({ length: selectedArts[index].boxes || 0 }).map((_, boxIndex) => (
                          <label key={boxIndex} className="observation-checkbox-label">
                            <input
                              type="checkbox"
                              checked={observationStates[index]?.[boxIndex] || false}
                              onChange={() => toggleObservation(index, boxIndex)}
                              className="observation-checkbox"
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedArts[index].levelUp && (
                    <div className="levelup-container">
                      <p><strong>Level Up:</strong> {selectedArts[index].levelUp}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default CharacterSheets;
